import { useCallback } from 'react'
import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'
import { zustandPlugin } from '@/modules/shared/openreplay'

type TabId = string
type ScopeKey = string
type TabMap = Record<ScopeKey, TabId>

type State = {
  tabs: TabMap
}

type Actions = {
  getTab: (scope: ScopeKey, fallback: TabId) => TabId
  setTab: (scope: ScopeKey, tab: TabId) => void
  clearTab: (scope: ScopeKey) => void
  clearAll: () => void
}

export const useTabsStore = create<State & Actions>()(
  devtools(
    persist(
      zustandPlugin((set, get) => ({
        tabs: {},
        getTab: (scope, fallback) => get().tabs[scope] ?? fallback,
        setTab: (scope, tab) =>
          set(
            (s) =>
              s.tabs[scope] === tab
                ? s
                : { tabs: { ...s.tabs, [scope]: tab } },
            false,
            'setTab',
          ),
        clearTab: (scope) =>
          set(
            (s) => {
              if (!(scope in s.tabs)) return s
              const next = { ...s.tabs }
              delete next[scope]
              return { tabs: next }
            },
            false,
            'clearTab',
          ),
        clearAll: () => set({ tabs: {} }, false, 'clearAll'),
      }), 'tabs'),
      {
        name: 'app.tabs.v1',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (s) => ({ tabs: s.tabs }),
      },
    ),
    { name: 'EasyTacho', store: 'tabs' },
  ),
)

export function usePersistentTab(scope: string, defaultTab: string) {
  const tab = useTabsStore((s) => s.tabs[scope] ?? defaultTab)
  const setTab = useTabsStore((s) => s.setTab)

  const set = useCallback(
    (nextTab: string) => {
      setTab(scope, nextTab)
    },
    [scope, setTab],
  )

  return [tab, set] as const
}
